import client from "./index";
import type {
  LoginResponse,
  RefreshTokenResponse,
  SignupData,
  CustomerSignupData,
  BusinessSignupData,
  PasswordResetRequest,
  PasswordResetVerify,
  PasswordReset,
  EmailVerification,
  AppleUserData,
  User,
} from "../types/auth";

/**
 * Register a new user with email and password
 */
export const register = async (data: SignupData): Promise<User> => {
  const response = await client.post<User>('/auth/register', data);
  return response.data;
};

/**
 * Register a customer and auto-enroll in a business loyalty program
 */
export const customerSignup = async (data: CustomerSignupData): Promise<User> => {
  const response = await client.post<User>('/auth/customer/signup', data);
  return response.data;
};

/**
 * Register a business owner
 */
export const businessSignup = async (data: BusinessSignupData): Promise<User> => {
  const response = await client.post<User>('/auth/business/signup', data);
  return response.data;
};

/**
 * Verify email with verification code
 */
export const verifyEmail = async (data: EmailVerification): Promise<User> => {
  const response = await client.post<User>('/auth/register/verify-email', data);
  return response.data;
};

/**
 * Resend email verification code
 */
export const resendVerificationCode = async (email: string): Promise<void> => {
  await client.post('/auth/register/resend-verification-code', { email });
};

/**
 * Login with email and password (OAuth2 password grant)
 */
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  const formData = new URLSearchParams();
  formData.append('username', email);
  formData.append('password', password);
  formData.append('grant_type', 'password');

  const response = await client.post<LoginResponse>('/auth/login', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });

  return response.data;
};

/**
 * Login with Google OAuth
 */
export const loginWithGoogle = async (idToken: string): Promise<LoginResponse> => {
  const response = await client.post<LoginResponse>('/auth/google', {
    id_token: idToken,
  });

  return response.data;
};

/**
 * Login with Apple OAuth
 */
export const loginWithApple = async (
  idToken: string,
  userData?: AppleUserData
): Promise<LoginResponse> => {
  const response = await client.post<LoginResponse>('/auth/apple', {
    id_token: idToken,
    user_data: userData
      ? { givenName: userData.first_name, familyName: userData.last_name }
      : null,
  });

  return response.data;
};

/**
 * Refresh access token using refresh token
 */
export const refreshToken = async (refreshToken: string): Promise<RefreshTokenResponse> => {
  const response = await client.post<RefreshTokenResponse>(
    '/auth/refresh',
    { refresh_token: refreshToken },
    {
      // Skip auth retry to prevent infinite loop
      _skipAuthRetry: true,
    } as any
  );

  return response.data;
};

/**
 * Logout and revoke refresh token
 */
export const logout = async (refreshToken: string): Promise<void> => {
  try {
    await client.post(
      '/auth/logout',
      { refresh_token: refreshToken },
      {
        // Skip auth retry to prevent issues during logout
        _skipAuthRetry: true,
      } as any
    );
  } catch (error) {
    // Ignore errors during logout
    console.error('Logout error:', error);
  }
};

/**
 * Request password reset code
 */
export const requestPasswordReset = async (data: PasswordResetRequest): Promise<void> => {
  await client.post('/auth/password-reset/request', data);
};

/**
 * Verify password reset code
 */
export const verifyPasswordResetCode = async (
  data: PasswordResetVerify
): Promise<{ reset_token: string; expires_at: string }> => {
  const response = await client.post<{ reset_token: string; expires_at: string }>(
    '/auth/password-reset/verify-code',
    data
  );

  return response.data;
};

/**
 * Resend password reset verification code
 */
export const resendPasswordResetCode = async (email: string): Promise<void> => {
  await client.post('/auth/password-reset/resend-verification-code', { email });
};

/**
 * Reset password with reset token
 */
export const resetPassword = async (data: PasswordReset): Promise<void> => {
  await client.post('/auth/password-reset/reset', data);
};
